/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { useState, useEffect } from "react";
import { useLocation, Link, useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiAvatar from "components/VuiAvatar";
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";
import axios from "axios";
import { useTranslation } from 'react-i18next';
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";
import {
  useVisionUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
  setDirection,
} from "context";
import team2 from "assets/images/team-2.jpg";
import logoSpotify from "assets/images/small-logos/logo-spotify.svg";

function DashboardNavbar({ absolute = false, light = false, isMini = false }) {
  const { t, i18n } = useTranslation();
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useVisionUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, direction } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [openProfileMenu, setOpenProfileMenu] = useState(false);
  const [openLanguageMenu, setOpenLanguageMenu] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const history = useHistory();

  const user = JSON.parse(localStorage.getItem("user") || "null");
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();

    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);
  const handleOpenProfileMenu = (event) => setOpenProfileMenu(event.currentTarget);
  const handleCloseProfileMenu = () => setOpenProfileMenu(false);

  const handleLogout = async () => {
    try {
      const response = await axios.get("https://api.csc-taxi.tech/api/admin/logout", {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': 'ar'
        }
      });

      if (response.data.status === 1) {
        // Clear local storage
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        // Remove authorization header
        delete axios.defaults.headers.common['Authorization'];
        // Redirect to sign in
        history.push("/authentication/sign-in");
      }
    } catch (error) {
      // If token is invalid or any other error, still clear local storage and redirect
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      delete axios.defaults.headers.common['Authorization'];
      history.push("/authentication/sign-in");
    }
  };

  const handleLanguageChange = (lang) => {
    console.log('Changing language to:', lang);
    console.log('Current language before change:', i18n.language);
    i18n.changeLanguage(lang);
    console.log('Language after change:', i18n.language);
    const newDirection = lang === 'ar' ? 'rtl' : 'ltr';
    setDirection(dispatch, newDirection);
    document.body.setAttribute("dir", newDirection);
    setOpenLanguageMenu(false);
  };

  // Render the language menu
  const renderLanguageMenu = () => (
    <Menu
      anchorEl={openLanguageMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openLanguageMenu)}
      onClose={() => setOpenLanguageMenu(false)}
      sx={{ mt: 2 }}
    >
      <MenuItem onClick={() => handleLanguageChange('en')}>
        <VuiBox display="flex" alignItems="center">
          <Icon sx={{ mr: 1 }}>flag</Icon>
          <VuiTypography variant="button" color="text" fontWeight="medium">
            {t('common.english')}
          </VuiTypography>
        </VuiBox>
      </MenuItem>
      <MenuItem onClick={() => handleLanguageChange('ar')}>
        <VuiBox display="flex" alignItems="center">
          <Icon sx={{ mr: 1 }}>flag</Icon>
          <VuiTypography variant="button" color="text" fontWeight="medium">
            {t('common.arabic')}
          </VuiTypography>
        </VuiBox>
      </MenuItem>
    </Menu>
  );

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem
        image={<img src={team2} alt="person" />}
        title={[t('notifications.newMessage'), t('notifications.from'), "Laur"]}
        date={t('notifications.minutesAgo', { count: 13 })}
        onClick={handleCloseMenu}
      />
      <NotificationItem
        image={<img src={logoSpotify} alt="person" />}
        title={[t('notifications.newAlbum'), t('notifications.by'), "Travis Scott"]}
        date={t('notifications.days', { count: 1 })}
        onClick={handleCloseMenu}
      />
      <NotificationItem
        color="text"
        image={
          <Icon fontSize="small" sx={{ color: ({ palette: { white } }) => white.main }}>
            payment
          </Icon>
        }
        title={["", t('notifications.paymentSuccessfullyCompleted')]}
        date={t('notifications.days', { count: 2 })}
        onClick={handleCloseMenu}
      />
    </Menu>
  );

  // Render the profile menu
  const renderProfileMenu = () => (
    <Menu
      anchorEl={openProfileMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      open={Boolean(openProfileMenu)}
      onClose={handleCloseProfileMenu}
      sx={{ mt: 2 }}
    >
      <VuiBox px={2} py={1}>
        <VuiTypography variant="button" color="text" fontWeight="medium">
          {user?.first_name + ' ' + user?.last_name}
        </VuiTypography>
      </VuiBox>
      <VuiBox px={2} py={1}>
        <VuiTypography
          variant="button"
          color="text"
          fontWeight="medium"
          sx={{ cursor: "pointer" }}
          onClick={() => {
            handleCloseProfileMenu();
            history.push("/profile");
          }}
        >
          {t('common.profile')}
        </VuiTypography>
      </VuiBox>
      <VuiBox px={2} py={1}>
        <VuiTypography
          variant="button"
          color="text"
          fontWeight="medium"
          sx={{ cursor: "pointer" }}
          onClick={handleLogout}
        >
          {t('common.signOut')}
        </VuiTypography>
      </VuiBox>
    </Menu>
  );

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <VuiBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route?.length - 1]} route={route} light={light} />
        </VuiBox>
        {isMini ? null : (
          <VuiBox sx={(theme) => navbarRow(theme, { isMini })}>
            <VuiBox>
              <IconButton
                sx={navbarIconButton}
                size="small"
                onClick={(e) => setOpenLanguageMenu(e.currentTarget)}
              >
                <Icon
                  sx={({ palette: { dark, white } }) => ({
                    color: light ? white.main : dark.main,
                  })}
                >
                  language
                </Icon>
              </IconButton>
              {renderLanguageMenu()}
            </VuiBox>
            
            {token ? (
              <IconButton
                sx={navbarIconButton}
                size="small"
                onClick={handleOpenProfileMenu}
              >
                <VuiAvatar
                  src={user?.profile_photo || team2}
                  alt="profile"
                  size="sm"
                  shadow="sm"
                />
              </IconButton>
            ) : (
              <Link to="/authentication/sign-in">
                <IconButton sx={navbarIconButton} size="small">
                  <Icon
                    sx={({ palette: { dark, white } }) => ({
                      color: light ? white.main : dark.main,
                    })}
                  >
                    account_circle
                  </Icon>
                </IconButton>
              </Link>
            )}
            {renderProfileMenu()}
          </VuiBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
